import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import {IonButton, IonCol, IonGrid, IonItem, IonRow} from '@ionic/react';
import {FORM_FIELDS} from './pin.types';
import Modal from '../../components/modal/modal.component';
import {StyledIonLabel} from '../../components/form/input/label.style';
import {StyledIonCheckbox} from '../../components/form/input/checkbox.style';
import Form from '../../components/form'
import {StyledPinRow} from './sign.style';
import CloseImage from "../../assets/images/e-remove.svg";
import {StyledModalContent} from "../../components/modal/modal.style";
import {disableSwipeToClose} from "../../utils/tools/modals";
import {StyledWiteButton} from "@components/button/button.style";
import {Preferences} from "@capacitor/preferences";
import {EventType} from "@enums/eventType";
import {useEvent} from "@hooks/useEvent";
import {validateEmail} from "@services/validation.service";
import greenCheck from "@assets/images/check-green.svg";
import {StyledInput, StyledInputEmail} from "@components/form/input/input.style";

export interface PinModalConfiguration {
    title: string;
    email?: string;
    buttonText: string;
    approveText?: string;
}

type PinModalProps = {
    configuration: PinModalConfiguration;
    isOpen: boolean;
    onClose: () => void;
    onSave: (code: string, email?: string) => void;
    formSubmitted?: boolean;
    loginWithEmail?: boolean;
    hiddenValue?: boolean;
};

export interface PinModalFormData {
    pin1: string;
    pin2: string;
    pin3: string;
    pin4: string;
    pin5: string;
    email: string;
};


const PinModal: React.FC<PinModalProps> = (props: PinModalProps) => {

    const {t} = useTranslation();
    const {listen} = useEvent();

    const [showSign, updateShowSign] = useState<boolean>(false);
    const [accepted, updateAccepted] = useState<boolean>(false);

    const {register, handleSubmit, formState: {isValid}, setValue, getValues, errors, watch} = useForm({
        mode: 'onChange'
    });

    const handleClose = () => {
        props.onClose();
    };

    useEffect(() => {
        handleOpen();
    });

    useEffect(() => {
        loadLastLoginEmail();
        listen(EventType["LOGIN.RESET_PIN"], () => {
            setPin('');
        });
    }, []);

    const loadLastLoginEmail = async () => {
        if (props.loginWithEmail) {
            let email = await Preferences.get({'key': 'last_login_email'});
            if (email && email.value) {
                setValue(FORM_FIELDS.EMAIL, email.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                });
            }
        }
    }

    const handleOpen = () => {
        updateShowSign(props.isOpen);
    };

    const onSubmit = (data: PinModalFormData) => {
        if (props.loginWithEmail) {
            onSubmitLogin(pin)
        } else {
            const onSave = props.onSave;
            if (onSave) {
                let code = data.pin1 + data.pin2 + data.pin3 + data.pin4 + data.pin5;
                onSave(code, data.email);
            }
        }
    };

    const onSubmitLogin = (code: string) => {
        const onSave = props.onSave;
        let email = getValues(FORM_FIELDS.EMAIL);
        if (onSave && email) {
            onSave(code, email);
        }
    };

    function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        const input = event.target as HTMLInputElement;

        // Blokuj wpisanie więcej niż jednego znaku (poza backspace)
        if (input.value.length >= 1 && event.key !== "Backspace") {
            event.preventDefault();
        }

        const form = input.form;
        const index = Array.prototype.indexOf.call(form, input);
        const isLastInput = !form?.elements[index + 1] ||
            (form.elements[index + 1] as HTMLInputElement).type !== "text";

        if (event.key === "Enter" && isLastInput) {
            // Submit formularza
            form?.requestSubmit(); // nowoczesna forma submitu
            event.preventDefault();
        }
    }

    function handleKeyDownEmail(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();
        }
    }

    function handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        const input = event.target as HTMLInputElement;
        const form = input.form;

        if (!form) return;

        const elements = Array.from(form.elements) as HTMLInputElement[];
        const index = elements.indexOf(input);

        // 🔹 Jeśli wpisano znak i to nie jest ostatnie pole, ustaw focus na następne
        if (input.value.length === 1) {
            const nextInput = elements[index + 1];

            if (nextInput && nextInput.tagName === "INPUT" && nextInput.name !== "accept" && nextInput.type !== "email") {
                setTimeout(() => nextInput.focus(), 10); // Krótkie opóźnienie
            }
        }

        // 🔹 Jeśli usunięto znak i to nie jest pierwsze pole, cofnij focus
        if (input.value.length === 0 && index > 0) {
            const prevInput = elements[index - 1];

            if (prevInput && prevInput.tagName === "INPUT" && prevInput.name !== "accept" && prevInput.type !== "email") {
                setTimeout(() => prevInput.focus(), 10);
            }
        }
    }

    const loginWithEmail = async () => {
        props.onClose();
    }

    const [pin, setPin] = useState('');

    const handleDigit = (digit: string) => {
        if (pin.length < 5) {
            let tmpPin = pin + digit;
            setPin(tmpPin);

            if (tmpPin.length === 5) {
                onSubmitLogin(tmpPin);
            }
        }
    };

    const handleBackspace = () => {
        setPin(pin.slice(0, -1));
    };

    const renderPinDots = () => {
        return Array.from({ length: 5 }).map((_, i) => (
            <span key={i} style={{ fontSize: '2rem', margin: '0 5px' }}>
                {i < pin.length ? '●' : '○'}
            </span>
        ));
    };

    let invalidEmailError = t("login.invalidEmail");
    const watchEmail = watch(FORM_FIELDS.EMAIL, '');


    return (
        <Modal
            isOpen={showSign}
            initialBreakpoint={0.85}
            breakpoints={[0, 0.85, 0.99]}
            canDismiss={disableSwipeToClose}
            onDidDismiss={() => handleClose()}>
            <Form.Container onSubmit={handleSubmit(onSubmit)}>
                <StyledModalContent>
                    <img src={CloseImage} className="close" onClick={() => handleClose()}/>
                    <h1 className="title full">{props.configuration.title}</h1>
                    {props.loginWithEmail &&
                        <>
                            <StyledInputEmail>
                                <StyledInput name={FORM_FIELDS.EMAIL}
                                             ref={register({
                                                 required: true,
                                                 validate: value =>
                                                     validateEmail(value) || invalidEmailError
                                             })}
                                             placeholder={t("login.inputEmailPlaceholder")}
                                             className={errors.email && 'hasErrors'}
                                             type="email"
                                             onKeyDown={handleKeyDownEmail}
                                />
                                {
                                    watchEmail && !errors.email &&
                                    <>
                                        <img src={greenCheck}/>
                                    </>
                                }
                            </StyledInputEmail>

                            <IonGrid>

                                <IonRow>
                                    {errors.email && errors.email.message && <p className="errorMessage">{errors.email.message}</p>}
                                </IonRow>

                                <IonRow style={{ display: 'flex', justifyContent: 'center', gap: '8px', paddingBottom: '20px', paddingTop: '16px'}}>
                                    {renderPinDots()}
                                </IonRow>

                                {[0, 1, 2, 3].map((row) => (
                                    <IonRow key={row}>
                                        {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].slice(row * 3, row * 3 + 3).map((digit, idx) => (
                                            <IonCol key={idx}>
                                                {digit ? (
                                                    <IonButton
                                                        style={{ width: '50px' }}
                                                        expand="block"
                                                        fill="outline"
                                                        onClick={() =>
                                                            digit === '⌫' ? handleBackspace() : handleDigit(digit)
                                                        }
                                                    >
                                                        {digit}
                                                    </IonButton>
                                                ) : (
                                                    <div style={{ height: '48px' }} />
                                                )}
                                            </IonCol>
                                        ))}
                                    </IonRow>
                                ))}

                                <IonRow>
                                    <StyledWiteButton onClick={e => loginWithEmail()}>{t("settingsTab.loginWithEmail")}</StyledWiteButton>
                                </IonRow>
                            </IonGrid>
                        </>
                    }
                    {!props.loginWithEmail &&
                        <>
                            <StyledPinRow>
                                <Form.PinInput name={FORM_FIELDS.PIN_1} placeholder=" " type={props.hiddenValue ? "password" : "text"} pattern="[0-9]*"
                                               inputMode="numeric"
                                               ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp} onKeyDown={handleKeyDown}/>
                                <Form.PinInput name={FORM_FIELDS.PIN_2} placeholder=" " type={props.hiddenValue ? "password" : "text"} pattern="[0-9]*"
                                               inputMode="numeric"
                                               ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp} onKeyDown={handleKeyDown}/>
                                <Form.PinInput name={FORM_FIELDS.PIN_3} placeholder=" " type={props.hiddenValue ? "password" : "text"} pattern="[0-9]*"
                                               inputMode="numeric"
                                               ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp} onKeyDown={handleKeyDown}/>
                                <Form.PinInput name={FORM_FIELDS.PIN_4} placeholder=" " type={props.hiddenValue ? "password" : "text"} pattern="[0-9]*"
                                               inputMode="numeric"
                                               ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp} onKeyDown={handleKeyDown}/>
                                <Form.PinInput name={FORM_FIELDS.PIN_5} placeholder=" " type={props.hiddenValue ? "password" : "text"} pattern="[0-9]*"
                                               inputMode="numeric"
                                               ref={register({required: true})} maxLength={1} onKeyUp={handleKeyUp} onKeyDown={handleKeyDown}/>
                            </StyledPinRow>

                            <div>
                                {
                                    props.configuration.approveText &&
                                    <IonItem lines="none">
                                        <StyledIonLabel
                                            className="ion-text-wrap">{props.configuration.approveText}</StyledIonLabel>
                                        <StyledIonCheckbox name={FORM_FIELDS.ACCEPT} slot="start" onIonChange={() => {
                                            updateAccepted(!accepted);
                                        }}/>
                                    </IonItem>
                                }
                            </div>

                            <br />

                            <Form.Button type="submit" disabled={!isValid || props.formSubmitted || (!!props.configuration.approveText && !accepted)}>{props.configuration.buttonText}</Form.Button>
                        </>
                    }
                </StyledModalContent>
            </Form.Container>
        </Modal>
    );
};

export default PinModal;